import {
	AfterViewInit,
	Component,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ButtonCellRendererComponent } from '@lmc-app/custom-table/button-cell-renderer/button-cell-renderer.component';
import { AdminSettingsService } from '@lmc-app/core/services/admin-settings.service';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { CustomTableComponent } from '../../custom-table/custom-table.component';

@Component({
	selector: 'app-external-api-clients',
	templateUrl: './external-api-clients.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: [],
	imports: [MatButton, CustomTableComponent],
})
export class ExternalApiClientsComponent implements AfterViewInit, OnInit {
	public gridApi_external: any;
	public gridApi_external_data: any;
	public gridColumnApi_external: any;
	defaultColDef_external: any;
	public columnDefs_external = [];

	showLoader: boolean;
	externalClientList: Subscription;
	externalClientData: any;
	public pinnedTopRowData: any[];
	public requiredFields = ['audience', 'scopes', 'client_status']; // Define your required fields
	constructor(
		private adminService: AdminSettingsService,
		private snackBar: MatSnackBar
	) {}
	ngOnInit(): void {
		this.getExternalClients();
	}
	ngAfterViewInit(): void {
		this.columnDefs_external = [
			{
				headerName: 'Client',
				field: 'audience',
				filter: 'agTextColumnFilter',
				editable: true,
			},
			{
				headerName: 'Client Id',
				field: 'client_id',
				filter: 'agTextColumnFilter',
			},
			{
				headerName: 'Scopes',
				field: 'scopes',
				filter: 'agTextColumnFilter',
				editable: true,
			},
			{
				headerName: 'Status',
				field: 'client_status',
				valueGetter: (params) => {
					return getClientStatus(params.data.client_status); // Initial display with clients
				},
				editable: true,
				cellEditor: 'agSelectCellEditor',
				cellEditorParams: {
					values: ['Active', 'Inactive'],
				},
				valueSetter: (params) => {
					return setNewClientStatus(params); // Set boolean for save/update
				},
			},
			{
				headerName: 'Created At',
				field: 'created_at',
				filter: 'agDateColumnFilter',
			},
			{
				headerName: 'Reset Secret',
				cellRenderer: ButtonCellRendererComponent,
				cellRendererParams: {
					label: 'Reset Secret',
					matIconLabel: 'lock_reset',
					clicked: (params: any) => {
						this.resetPassword('clientSecret', params);
					},
				},
			},
			{
				headerName: 'Update',
				cellRenderer: ButtonCellRendererComponent,
				cellRendererParams: {
					label: 'Update',
					matIconLabel: 'save',
					clicked: (params: any) => {
						console.log(params, '+++');
						this.updateClientEntries('clientData', params);
					},
				},
			},
		];
		this.defaultColDef_external = {
			resizable: true,
		};
	}

	getExternalClients() {
		this.externalClientList = this.adminService
			.getExternalClients()
			.subscribe((res) => {
				this.externalClientData = res;
				if (this.externalClientData === false) {
					this.externalClientData = [];
					console.log('ZOHO error!');
				} else {
					this.gridApi_external?.setRowData(this.externalClientData);
					this.gridApi_external_data = this.externalClientData;
				}
			});
	}

	/**
	 *
	 * @returns column fields with empty values
	 */
	createNewRowData() {
		const newRow: any = {};
		this.columnDefs_external.forEach((colDef) => {
			if (colDef.field) {
				newRow[colDef.field] = null;
			}
		});
		newRow['scopes'] = 'lmc.read,lmc.create';
		newRow['client_status'] = true;
		newRow['isNew'] = true;
		return newRow;
	}

	addBlankRowAndEdit() {
		const firstDisplayedRowNode =
			this.gridApi_external.getDisplayedRowAtIndex(0);
		if (firstDisplayedRowNode?.data?.isNew) {
			return;
		}
		const newBlankRow = this.createNewRowData();
		const res = this.gridApi_external.applyTransaction({
			add: [newBlankRow], //  Add blank row
			addIndex: 0, // top row
		});
		const [addedRow] = res?.add;
	}

	getAllMainRowData() {
		if (
			this.gridApi_external &&
			this.gridApi_external.getEditingCells().length > 0
		) {
			this.gridApi_external.stopEditing();
		}
		const rowData = [];
		this.gridApi_external.forEachNode((node) => {
			if (!('row_id' in node.data)) {
				rowData.push(node.data);
			}
		});
		return rowData;
	}

	saveClientEntries() {
		const clients = this.getAllMainRowData();
		if (clients.length === 0 || clients[0]['audience'] === null) {
			this.loadMessageSnackBar('No client data to save.');
			return;
		}
		this.adminService.createExternalApiClient(clients).subscribe({
			next: () => {
				this.getExternalClients();
				this.loadMessageSnackBar('Save successful.');
			},
			error: (err) => {
				this.loadMessageSnackBar(`Save failed. ${err.error.message}`);
			},
		});
	}

	updateClientEntries(fieldName: string, rowData: any) {
		this.adminService.updateExternalApiClient(fieldName, rowData).subscribe({
			next: () => {
				this.getExternalClients();
				this.loadMessageSnackBar('Client data update successful.');
			},
			error: (err) => {
				this.loadMessageSnackBar(`Failed to update ${err.error.message}.`);
			},
		});
	}

	validateRowEntry() {
		const obj = this.getAllMainRowData();
		return Object.keys(obj).filter((key) => {
			const value = obj[key];
			return value === null || value === undefined || value === '';
		});
	}

	resetPassword(field: string, rowData: any) {
		this.adminService.updateExternalApiClient(field, rowData).subscribe({
			next: () => {
				this.getExternalClients();
				this.loadMessageSnackBar('Client secret update successful.');
			},
			error: (err) => {
				this.loadMessageSnackBar(
					`Failed to update client secret ${err.error.message}.`
				);
			},
		});
	}

	onGridReady(params: any): void {
		this.gridApi_external = params.api;
		this.showLoader = true;
		this.gridColumnApi_external = params.columnApi;
		this.gridApi_external?.setRowData(this.externalClientData);
		this.gridApi_external_data = this.externalClientData;
	}

	loadMessageSnackBar(message: string) {
		console.log(message, '++++');
		this.snackBar.open(`${message}`, 'Close', {
			duration: 3000,
			horizontalPosition: 'center',
			verticalPosition: 'bottom',
		});
	}

	ngOnDestroy() {
		this.externalClientList.unsubscribe();
	}
}

const getClientStatus = (status: boolean) => {
	if (status === true) {
		return 'Active';
	}
	return 'Inactive';
};

const setNewClientStatus = (params: any) => {
	const newValue = params.newValue; // String value
	const data = params.data;
	if (newValue === 'Active') {
		data.client_status = true;
	} else if (newValue === 'Inactive') {
		data.client_status = false;
	}
	return true;
};
